import CopyFromLayerButton from "./CopyFromLayerButton";
import * as policySupport from "./support";
import { makeEndorsementLabel } from "./support";
import { DragIndicator as DragIndicatorIcon } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import SendIcon from "@mui/icons-material/SendRounded";
import UploadIcon from "@mui/icons-material/Upload";
import {
  Alert,
  Autocomplete,
  Checkbox,
  Link,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { useTheme } from "@mui/material/styles";
import * as arrays from "common/arrays";
import Component from "components/Component";
import BigLoader from "components/common/BigLoader";
import Button from "components/common/Button";
import DefaultLoader from "components/common/DefaultLoader";
import Disabled from "components/common/Disabled";
import ExtraInput from "components/common/ExtraInput";
import HiddenIfReadOnly from "components/common/HiddenIfReadOnly";
import Visible from "components/common/Visible";
import SendOverrideToTemplateRequestsDialog from "components/summary/templates/SendOverrideToTemplateRequestsDialog";
import { useTemplateOverride } from "components/summary/templates/useTemplateOverride";
import * as jsConfig from "config";
import * as labels from "config.labels";
import {
  ENDORSEMENT_AUTO_TRIA_FORMS,
  ENDORSEMENT_SORT_ORDER,
  TEMPLATE_REQUESTS_EDIT_FLOW_V2,
} from "flags";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDrag, useDrop } from "react-dnd";
import { useDispatch, useSelector } from "react-redux";
import * as docGenService from "services/docGenService";
import * as endorsementStore from "store/endorsements";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import * as persistenceSelectors from "store/selectors/persistence/persistenceSelectors";
import * as pricingSelectors from "store/selectors/pricing/pricingSelectors";
import * as submissionSelectors from "store/selectors/submission/submissionSelector";
import * as userSelectors from "store/selectors/user/userSelectors";

const DND_TYPE_ENDORSEMENT = "ENDORSEMENT";

const isUnknownEndorsement = ({ config, endorsement }) => {
  const { title } = endorsement ?? {};
  const endorsementConfig = config?.items?.[title] ?? {};
  return (
    !endorsementConfig.hasTemplate && !endorsementConfig.isTemplateOptional
  );
};

const LinkButton = ({ onClick, label, disabled = false }) => (
  <Disabled ifReadOnly>
    <Link
      component={"button"}
      variant={"body2"}
      color={"secondary"}
      onClick={disabled ? undefined : onClick}
      sx={{
        display: "block",
        marginBottom: "8px",
        color: disabled ? "text.disabled" : undefined,
        pointerEvents: disabled ? "none" : "auto",
      }}
    >
      {label}
    </Link>
  </Disabled>
);

const SelectedEndorsementEntry = ({
  index,
  config,
  endorsements,
  setEndorsements,
  layerId,
  submissionId,
  resolvedSubmission,
  templateOverridesByLayer,
  refetchTemplateOverrides,
  fillTemplate,
  isFillTemplateLoading,
  setTemplateOverride,
  removeTemplateOverride,
  getTemplateSummary,
  userEmail,
}) => {
  const theme = useTheme();
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const translateLabels = staticDataHooks.useTranslator("uiLabels");

  const endorsement = endorsements?.[index];
  const template = config?.items?.[endorsement?.title]?.template;
  const overrideRecord = template?.id
    ? templateOverridesByLayer?.[template.id]
    : null;

  const { upload, download, remove, loadingAction } = useTemplateOverride({
    template,
    submissionId,
    layerId,
    setTemplateOverride,
    removeTemplateOverride,
    refetchTemplateOverrides,
  });

  const [sendOverrideOpen, setSendOverrideOpen] = useState(false);
  const [sendOverrideSuccess, setSendOverrideSuccess] = useState(false);

  const ensureDocx = (filename, fallbackTitle) => {
    if (!filename) return `${fallbackTitle ?? "Template"}.docx`;
    const lower = filename.toLowerCase();
    return lower.endsWith(".docx") ? filename : `${filename}.docx`;
  };

  const mousedownOnInput = useRef(false);

  const [, dragRef, dragPreviewRef] = useDrag(
    () => ({
      type: DND_TYPE_ENDORSEMENT,
      item: { index },
      canDrag: () => !mousedownOnInput.current,
    }),
    [index]
  );

  const [, dropRef] = useDrop(
    () => ({
      accept: DND_TYPE_ENDORSEMENT,
      drop: (item) =>
        setEndorsements(
          arrays.move(endorsements, {
            fromIndex: item.index,
            toIndex: index,
          })
        ),
    }),
    [index, endorsements, setEndorsements]
  );

  const deselectEndorsement = useCallback(() => {
    setEndorsements([
      ...(endorsements?.slice(0, index) ?? []),
      ...(endorsements?.slice(index + 1) ?? []),
    ]);
  }, [endorsements, index, setEndorsements]);

  const isBusy = loadingAction !== null || isFillTemplateLoading;

  const downloadFilled = async () => {
    if (!template?.id || !resolvedSubmission) return;

    const ext = template?.form === "PDF_ENDORSEMENT" ? "pdf" : "docx";
    const filename = `${endorsement?.title}-filled.${ext}`;

    await fillTemplate({
      templateId: template.id,
      templateForm: template?.form ?? "WORD_JINJA_ENDORSEMENT",
      content: { submission: resolvedSubmission, layerId },
      filename,
      endorsementIndex: index,
    });
  };

  const onFileSelected = async (e) => {
    try {
      const file = e?.target?.files?.[0];
      if (!file) return;

      await upload(file);
    } finally {
      if (e?.target) {
        e.target.value = null;
      }
    }
  };

  const extraInputs = config?.items?.[endorsement?.title]?.extraInputs;
  const updateExtraInput = (key, value) =>
    setEndorsements(
      endorsements?.map((e, i) =>
        i === index
          ? {
              ...e,
              [key]: value,
            }
          : e
      )
    );

  return (
    <Stack
      direction={"column"}
      spacing={1}
      ref={(el) => {
        dragRef(el);
        dropRef(el);
        dragPreviewRef(el);
      }}
      onMouseDown={(e) => {
        mousedownOnInput.current = !!e.target.closest(
          "input, textarea, select, [contenteditable='true'], .MuiFormControl-root"
        );
      }}
      onMouseUp={() => {
        mousedownOnInput.current = false;
      }}
      onDragOver={(e) => {
        if (!template?.id) return;
        if (mousedownOnInput.current) return;
        e.preventDefault();
        setIsDragOver(true);
      }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragOver(false);
        upload(e.dataTransfer.files?.[0]);
      }}
      sx={{
        position: "relative",
        borderRadius: 1,
        ...(isDragOver && {
          outline: "2px dashed #FB923C",
          outlineOffset: "4px",
          backgroundColor: "#FFF7ED",
        }),
      }}
    >
      <Stack direction={"row"} justifyContent={"space-between"} spacing={1}>
        <Stack
          direction={"row"}
          alignItems={"center"}
          spacing={1}
          sx={{ minWidth: 0 }}
        >
          <Disabled ifReadOnly>
            <Checkbox checked onClick={deselectEndorsement} />
          </Disabled>
          <Typography variant={"body"} noWrap>
            {makeEndorsementLabel({ config, endorsement })}
          </Typography>
        </Stack>

        {template?.id && template?.file_id && (
          <Stack direction={"row"} alignItems={"center"} spacing={1}>
            <input
              type={"file"}
              hidden
              ref={fileInputRef}
              accept={
                ".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              }
              onChange={onFileSelected}
            />

            <Button
              size={"small"}
              startIcon={
                loadingAction === "download" ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "16px",
                    }}
                  >
                    <DefaultLoader color={theme.brand.white} />
                  </div>
                ) : (
                  <DownloadIcon />
                )
              }
              disabled={isBusy}
              onClick={() =>
                download(
                  template?.file_id,
                  ensureDocx(template?.filename, template?.title)
                )
              }
              variant={"contained"}
            >
              {"Template"}
            </Button>

            <Button
              size={"small"}
              startIcon={
                isFillTemplateLoading ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "16px",
                    }}
                  >
                    <DefaultLoader color={theme.brand.white} />
                  </div>
                ) : (
                  <DownloadIcon />
                )
              }
              disabled={isBusy}
              onClick={downloadFilled}
              variant={"contained"}
            >
              {"Filled"}
            </Button>

            <Button
              size={"small"}
              startIcon={
                loadingAction === "upload" ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "16px",
                    }}
                  >
                    <DefaultLoader color={theme.brand.white} />
                  </div>
                ) : (
                  <UploadIcon />
                )
              }
              disabled={isBusy}
              onClick={() => fileInputRef.current?.click()}
              variant={"contained"}
            >
              {"Override"}
            </Button>

            <DragIndicatorIcon fontSize={"small"} />
          </Stack>
        )}
      </Stack>

      {config?.items?.[endorsement?.title]?.overrideRequired &&
        !overrideRecord?.file_id && (
          <Alert
            style={{ marginRight: "20px" }}
            severity={"warning"}
            icon={true}
          >
            {translateLabels(labels.ENDORSEMENTS_OVERRIDE_REQUIRED, {
              default:
                "This template requires an override to work. Please upload an overridden template.",
            })}
          </Alert>
        )}
      {overrideRecord?.file_id && (
        <Alert
          style={{ marginRight: "20px" }}
          severity={"info"}
          action={
            <Stack direction={"row"} spacing={1}>
              <Button
                size={"small"}
                startIcon={
                  loadingAction === "download" ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "16px",
                      }}
                    >
                      <DefaultLoader color={theme.brand.white} />
                    </div>
                  ) : (
                    <DownloadIcon />
                  )
                }
                disabled={isBusy}
                onClick={() =>
                  download(
                    overrideRecord?.file_id,
                    ensureDocx(template?.filename, template?.title)
                  )
                }
                variant={"contained"}
              >
                {"Download"}
              </Button>

              <Visible byTag={TEMPLATE_REQUESTS_EDIT_FLOW_V2}>
                <Button
                  size={"small"}
                  startIcon={<SendIcon />}
                  disabled={isBusy}
                  onClick={() => setSendOverrideOpen(true)}
                  variant={"contained"}
                >
                  {"Send to template requests"}
                </Button>
              </Visible>

              <Button
                size={"small"}
                color={"error"}
                startIcon={
                  loadingAction === "remove" ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "16px",
                      }}
                    >
                      <DefaultLoader color={theme.brand.white} />
                    </div>
                  ) : (
                    <DeleteIcon />
                  )
                }
                disabled={isBusy}
                onClick={remove}
                variant={"contained"}
              >
                {"Remove"}
              </Button>
            </Stack>
          }
        >
          {"This template has been overridden."}
        </Alert>
      )}

      <SendOverrideToTemplateRequestsDialog
        open={sendOverrideOpen}
        onClose={() => setSendOverrideOpen(false)}
        template={template}
        overrideFileId={overrideRecord?.file_id}
        submissionId={submissionId}
        layerId={layerId}
        onSent={() => setSendOverrideSuccess(true)}
      />

      <Snackbar
        open={sendOverrideSuccess}
        autoHideDuration={4000}
        onClose={() => setSendOverrideSuccess(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={"success"}
          variant={"filled"}
          onClose={() => setSendOverrideSuccess(false)}
        >
          {"Sent to template requests."}
        </Alert>
      </Snackbar>

      {extraInputs && (
        <Stack direction={"column"} sx={{ paddingLeft: 10 }}>
          {extraInputs.map((extraInput) => (
            <ExtraInput
              key={extraInput.key}
              definition={extraInput}
              value={endorsement?.[extraInput.key]}
              setValue={(newValue) =>
                updateExtraInput(extraInput.key, newValue)
              }
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
};

const SelectedEndorsements = ({
  config,
  endorsements,
  setEndorsements,
  userEmail,
  sortEnabled,
  ...props
}) => {
  const translateLabels = staticDataHooks.useTranslator("uiLabels");

  const resetOrdering = () =>
    setEndorsements(
      policySupport.sortEndorsementsByConfigOrder({ config, endorsements })
    );

  return (
    <Stack direction={"column"} spacing={1}>
      {sortEnabled && (endorsements ?? []).length > 1 && (
        <Stack direction={"row"} justifyContent={"flex-end"}>
          <LinkButton
            onClick={resetOrdering}
            label={translateLabels(labels.ENDORSEMENTS_RESET_ORDERING, {
              default: "Reset Ordering",
            })}
          />
        </Stack>
      )}
      {(endorsements ?? [])
        .map((endorsement, index) => [endorsement, index])
        .filter(
          ([endorsement]) => !isUnknownEndorsement({ config, endorsement })
        )
        .map(([endorsement, index]) => (
          <SelectedEndorsementEntry
            key={`${endorsement.title}-${index}`}
            index={index}
            config={config}
            endorsements={endorsements}
            setEndorsements={setEndorsements}
            userEmail={userEmail}
            {...props}
          />
        ))}
    </Stack>
  );
};

const AvailableEndorsementsSelector = ({
  config,
  endorsements,
  addEndorsements,
}) => {
  const selectedTitles = useMemo(
    () => new Set(endorsements?.map(({ title }) => title)),
    [endorsements]
  );

  const entries = useMemo(
    () =>
      Object.entries(config?.items ?? {})
        .filter(
          ([title, { deprecated, template }]) =>
            !deprecated &&
            (!selectedTitles.has(title) || !!template?.multi_entry_allowed)
        )
        .map(([title, { label }]) => ({ title, label }))
        .sort((a, b) =>
          makeEndorsementLabel({ config, endorsement: a }).localeCompare(
            makeEndorsementLabel({ config, endorsement: b })
          )
        ),
    [config, selectedTitles]
  );

  return (
    <Autocomplete
      value={null}
      onChange={(_, value, reason) => {
        if (reason === "selectOption") {
          addEndorsements([value.title]);
        }
      }}
      options={entries}
      getOptionKey={({ title }) => title}
      getOptionLabel={(endorsement) =>
        makeEndorsementLabel({ config, endorsement })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          hiddenLabel
          variant={"filled"}
          InputLabelProps={{ shrink: true }}
        />
      )}
      autoHighlight
      disableClearable
      blurOnSelect
      fullWidth
    />
  );
};

const AvailableEndorsements = ({
  config,
  endorsements,
  setEndorsements,
  buildDefaults,
  sortEnabled,
}) => {
  const translateLabels = staticDataHooks.useTranslator("uiLabels");

  const selectedTitles = new Set(
    (endorsements ?? []).map(({ title }) => title)
  );
  const selectableTitles = (config?.defaultVisibleEndorsements ?? []).filter(
    (title) => !selectedTitles.has(title) && config?.items?.[title]
  );

  const defaultTitles = config?.defaultSelectedEndorsements ?? [];

  const addEndorsements = (titles) => {
    if (!titles?.length) {
      return;
    }
    const newEndorsements = titles.map(policySupport.makeEndorsement(config));
    if (sortEnabled) {
      // Slot each new endorsement into its sorted position without re-sorting
      // the rest of the list (out-of-place endorsements are left untouched).
      setEndorsements(
        newEndorsements.reduce(
          (list, endorsement) =>
            policySupport.insertEndorsementBySortOrder({
              config,
              endorsements: list,
              endorsement,
            }),
          endorsements ?? []
        )
      );
    } else {
      setEndorsements([...(endorsements ?? []), ...newEndorsements]);
    }
  };

  return (
    <Paper sx={{ width: "100%" }}>
      <Stack direction={"column"} spacing={2} sx={{ padding: 2 }}>
        <Stack direction={"row"} justifyContent={"space-between"}>
          <Typography variant={"subtitle1"}>
            {translateLabels(labels.AVAILABLE_ENDORSEMENTS_TITLE, {
              default: "Available Endorsements",
            })}
          </Typography>
          <Stack direction={"row"} spacing={1}>
            {selectableTitles.length > 0 && (
              <LinkButton
                onClick={() => addEndorsements(selectableTitles)}
                label={translateLabels(labels.ENDORSEMENTS_SELECT_ALL, {
                  default: "Select All",
                })}
              />
            )}
            {defaultTitles.length > 0 && (
              <LinkButton
                onClick={() => setEndorsements(buildDefaults())}
                label={translateLabels(labels.ENDORSEMENTS_RESET_TO_DEFAULT, {
                  default: "Reset to Default",
                })}
              />
            )}
          </Stack>
        </Stack>

        <Stack direction={"column"} spacing={1}>
          {selectableTitles.map((title) => (
            <Stack
              key={title}
              direction={"row"}
              onClick={() => addEndorsements([title])}
              alignItems={"center"}
              spacing={1}
              sx={{ cursor: "pointer" }}
            >
              <Checkbox checked={false} />
              <Typography variant={"body"}>
                {policySupport.makeEndorsementLabel({
                  config,
                  endorsement: { title },
                })}
              </Typography>
            </Stack>
          ))}
        </Stack>

        <AvailableEndorsementsSelector
          config={config}
          endorsements={endorsements}
          addEndorsements={addEndorsements}
        />
      </Stack>
    </Paper>
  );
};

const UnknownEndorsements = ({ config, endorsements, setEndorsements }) => {
  const translateLabels = staticDataHooks.useTranslator("uiLabels");

  const unknownEndorsements = (endorsements ?? []).filter((endorsement) =>
    isUnknownEndorsement({ config, endorsement })
  );

  if (!unknownEndorsements.length) return <></>;

  return (
    <Paper sx={{ width: "100%" }}>
      <Stack
        direction={"column"}
        spacing={2}
        sx={{ padding: 2, background: "mistyrose" }}
      >
        <Typography variant={"subtitle1"}>
          {translateLabels(labels.UNKNOWN_ENDORSEMENTS_TITLE, {
            default: "Unknown Endorsements",
          })}
        </Typography>

        <Typography variant={"subtitle2"} color={"secondary"}>
          {translateLabels(labels.UNKNOWN_ENDORSEMENTS_DESCRIPTION, {
            default:
              "The following selected endorsements are not available, please contact: ",
          })}
          <Link
            href={`mailto:${
              jsConfig.SUPPORT_EMAIL
            }?subject=${encodeURIComponent("Unknown Endorsements")}`}
          >
            {jsConfig.SUPPORT_EMAIL}
          </Link>
          {"."}
        </Typography>

        {unknownEndorsements.map((endorsement) => (
          <Stack key={endorsement.title} direction={"row"} spacing={2}>
            <Disabled ifReadOnly>
              <IconButton
                onClick={() =>
                  setEndorsements(
                    endorsements.filter((e) => e.title !== endorsement.title)
                  )
                }
              >
                <DeleteIcon color={"primary"} />
              </IconButton>
            </Disabled>
            <Typography variant={"body"}>
              {policySupport.makeEndorsementLabel({ config, endorsement })}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
};

export const Endorsements = ({ layerId }) => {
  const translateLabels = staticDataHooks.useTranslator("uiLabels");
  const dispatch = useDispatch();

  const submissionId = useSelector(persistenceSelectors.selectSubmissionId);
  const resolvedSubmission = useSelector(
    submissionSelectors.selectResolvedSubmissionWithoutInput
  );
  const userEmail = useSelector(userSelectors.selectUserEmail);

  const {
    data: templateOverrides,
    refetch: refetchTemplateOverrides,
  } = docGenService.useGetTemplateOverridesQuery(
    { submissionId },
    { skip: !submissionId }
  );

  const templateOverridesByLayer = templateOverrides?.[layerId] ?? {};

  const [
    fillTemplate,
    { isLoading: isFillTemplateLoading },
  ] = docGenService.useFillTemplateMutation();
  const [setTemplateOverride] = docGenService.useSetTemplateOverrideMutation();
  const [
    removeTemplateOverride,
  ] = docGenService.useRemoveTemplateOverrideMutation();
  const [getTemplateSummary] = docGenService.useGetTemplateSummaryMutation();

  const rawEndorsements = useSelector(endorsementStore.select)
    ?.endorsementsByLayer?.[layerId];
  const endorsements = rawEndorsements ?? [];

  const predicateMatch = useSelector(
    (state) => state.endorsements?.predicateMatchByLayer?.[layerId]
  );

  const {
    config,
    predicateMatch: currentPredicateMatch,
    isLoading,
  } = policySupport.usePolicyConfig({ layerId });

  const {
    isVisible: isSortVisible,
    isLoading: isSortFlagLoading,
  } = staticDataHooks.useIsVisible(ENDORSEMENT_SORT_ORDER);
  const sortEnabled = !!isSortVisible;

  const {
    isVisible: isAutoTriaVisible,
    isLoading: isAutoTriaFlagLoading,
  } = staticDataHooks.useIsVisible(ENDORSEMENT_AUTO_TRIA_FORMS);
  const autoTriaEnabled = !!isAutoTriaVisible;

  const resolvedLayer = useSelector((state) =>
    pricingSelectors.selectResolvedLayers(state)?.find((l) => l?.id === layerId)
  );
  const layerPolicyType = resolvedLayer?.policyType;
  const layerTriaStatus = resolvedLayer?.triaStatus;
  // Remembers the last TRIA status we acted on per layer, so we only reconcile
  // on an actual change made in-session and never silently rewrite the forms of
  // a submission just because it was opened.
  const lastTriaStatusByLayer = useRef({});

  const setEndorsements = useCallback(
    (newEndorsements, newPredicateMatch) =>
      dispatch(
        endorsementStore.setLayerEndorsements({
          layerId,
          endorsements: newEndorsements,
          predicateMatch: newPredicateMatch,
        })
      ),
    [dispatch, layerId]
  );

  // Builds the default endorsement list, applying sortOrder ordering only when
  // the sort feature is enabled for this company (visibility tag, default off).
  const buildDefaults = useCallback(() => {
    const defaults = policySupport.makeDefaultEndorsements(config);
    return sortEnabled
      ? policySupport.sortEndorsementsByConfigOrder({
          config,
          endorsements: defaults,
        })
      : defaults;
  }, [config, sortEnabled]);

  useEffect(() => {
    if (
      !isSortFlagLoading &&
      rawEndorsements == null &&
      config?.defaultSelectedEndorsements?.length
    ) {
      setEndorsements(buildDefaults(), currentPredicateMatch);
    }
  }, [
    isSortFlagLoading,
    rawEndorsements,
    config,
    buildDefaults,
    setEndorsements,
    currentPredicateMatch,
  ]);

  // Compare on the normalised shape: predicateMatch values persisted before the
  // array form was canonical are bare objects, and re-wrapping them must not
  // count as a change (otherwise opening any existing layer rewrites the store).
  useEffect(() => {
    if (
      rawEndorsements != null &&
      JSON.stringify(policySupport.normalisePredicateMatch(predicateMatch)) !==
        JSON.stringify(currentPredicateMatch)
    ) {
      dispatch(
        endorsementStore.setLayerEndorsements({
          layerId,
          endorsements: rawEndorsements,
          predicateMatch: currentPredicateMatch,
        })
      );
    }
  }, [
    dispatch,
    layerId,
    rawEndorsements,
    predicateMatch,
    currentPredicateMatch,
  ]);

  // Auto TRIA forms: when the TRIA status changes on a layer that already has an
  // endorsement list, swap the TRIA forms to match, preserving every other form.
  // This removes the manual "Reset to Default" step the customer had to use. Only
  // fires on an in-session change (never on first load) and only for companies
  // with the visibility flag on.
  useEffect(() => {
    if (
      !autoTriaEnabled ||
      isAutoTriaFlagLoading ||
      rawEndorsements == null ||
      layerPolicyType == null ||
      layerTriaStatus == null
    ) {
      return;
    }
    const previous = lastTriaStatusByLayer.current[layerId];
    lastTriaStatusByLayer.current[layerId] = layerTriaStatus;
    if (previous === undefined || previous === layerTriaStatus) {
      return;
    }
    const reconciled = policySupport.reconcileTriaForms({
      config,
      endorsements: rawEndorsements,
      policyType: layerPolicyType,
      triaStatus: layerTriaStatus,
      sortEnabled,
    });
    if (reconciled !== rawEndorsements) {
      setEndorsements(reconciled, currentPredicateMatch);
    }
  }, [
    autoTriaEnabled,
    isAutoTriaFlagLoading,
    rawEndorsements,
    layerId,
    layerPolicyType,
    layerTriaStatus,
    config,
    sortEnabled,
    setEndorsements,
    currentPredicateMatch,
  ]);

  return (
    <Component
      title={translateLabels(labels.ENDORSEMENTS, {
        default: "Endorsements",
      })}
      options={<CopyFromLayerButton layerId={layerId} type={"ENDORSEMENTS"} />}
    >
      {isLoading ? (
        <BigLoader />
      ) : (
        <Stack direction={"column"} spacing={2}>
          <UnknownEndorsements
            config={config}
            endorsements={endorsements}
            setEndorsements={setEndorsements}
          />
          <SelectedEndorsements
            config={config}
            endorsements={endorsements}
            setEndorsements={setEndorsements}
            layerId={layerId}
            submissionId={submissionId}
            resolvedSubmission={resolvedSubmission}
            templateOverridesByLayer={templateOverridesByLayer}
            refetchTemplateOverrides={refetchTemplateOverrides}
            fillTemplate={fillTemplate}
            isFillTemplateLoading={isFillTemplateLoading}
            setTemplateOverride={setTemplateOverride}
            removeTemplateOverride={removeTemplateOverride}
            getTemplateSummary={getTemplateSummary}
            userEmail={userEmail}
            sortEnabled={sortEnabled}
          />
          <HiddenIfReadOnly>
            <AvailableEndorsements
              config={config}
              endorsements={endorsements}
              setEndorsements={setEndorsements}
              buildDefaults={buildDefaults}
              sortEnabled={sortEnabled}
            />
          </HiddenIfReadOnly>
        </Stack>
      )}
    </Component>
  );
};

export default Endorsements;
